import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { map, Observable, tap } from 'rxjs';
import { nebulaConst } from '@interfaces/nebulaConst';
import { IUserSession } from '@interfaces/nebula';

export interface IUserSessionConfiguration {
  lifetime: number;
  allowRefresh: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserSessionsService {
  private http = inject(HttpClient);
  private logger = inject(LoggerService);
  private baseUrl = inject<string>('BASE_URL' as never);

  private apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = `${this.baseUrl}api/v1.2/user-sessions`;
    this.logger.info('UserSessions Service started');
  }

  getCurrentUserSessions(): Observable<IUserSession[]> {
    return this.http.get<IUserSession[]>(`${this.apiBaseUrl}/list`, { headers: this.headers() }).pipe(
      tap({
        error: error => {
          this.logger.error('Fehler beim Laden der Sitzungen des aktuellen Benutzers', { error });
        }
      })
    );
  }

  getUserSessions(id: string): Observable<IUserSession[]> {
    return this.http.get<IUserSession[]>(`${this.apiBaseUrl}/list/${id}`, { headers: this.headers() }).pipe(
      tap({
        error: error => {
          this.logger.error('Fehler beim Laden der Sitzungen des Benutzers', { error });
        }
      })
    );
  }

  deleteUserSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/${sessionId}`, { headers: this.headers() }).pipe(
      tap({
        error: error => {
          this.logger.error('Fehler beim Löschen der Sitzung des Benutzers', { error });
        }
      })
    );
  }

  getUserSessionsConfiguration(): Observable<IUserSessionConfiguration> {
    return this.http.get<IUserSessionConfiguration>(`${this.apiBaseUrl}/configuration`, { headers: this.headers() }).pipe(
      map(data => ({
        lifetime: this.convertToSeconds(data.lifetime as unknown as string),
        allowRefresh: data.allowRefresh
      })),
      tap({
        error: error => {
          this.logger.error('Fehler beim Laden der UserSessionsConfiguration', { error });
        }
      })
    );
  }

  private convertToSeconds(time: string): number {
    const parts = time?.split(':')?.map(Number);

    if (!parts || parts.length !== 3) {
      return nebulaConst.sessionTimeout;
    }

    const [hours, minutes, seconds] = parts;
    return hours * 3600 + minutes * 60 + seconds;
  }

  private headers(): HttpHeaders {
    return new HttpHeaders({
      accept: 'application/json'
    });
  }
}
